import { store as globalStore } from 'Models/Store';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { GiftVoucherStepIndicator } from 'Views/Components/_HumanWritten/PurchaseGiftCertificate/GiftVoucherStepIndicator';
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import AnytimeTicketProvider, { useAnytimeTicketStore, useAnytimeTicketStoreApi } from '../State/AnytimeTicketContext';
import useAnytimeTicketNavigationGuard from '../Hooks/useAnytimeTicketNavigationGuard';
import AnytimeTicketLoginStep from './AnytimeTicketLoginStep';
import AnytimeTicketPaymentStep from './AnytimeTicketPaymentStep';
import AnytimeTicketProductStep from './AnytimeTicketProductStep';
import AnytimeTicketRegisterCustomerStep from './AnytimeTicketRegisterCustomerStep';
import AnytimeTicketTermsStep from './AnytimeTicketTermsStep';
import AnytimeTicketReviewStep from './AnytimeTicketReviewStep';
import AnytimeTicketSuccessStep from './AnytimeTicketSuccessStep';
import AnytimeTicketTimer from './AnytimeTicketTimer';
import { PaymentCheckOutProvider } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import { useProductList } from 'Modules/Products/Hooks/useProductList';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { getMessageByKey } from 'Stores/LanguageStore';

interface StepDefinition {
	label: string;
	icon?: string;
	Content: React.ComponentType<any>;
	Footer: React.ComponentType<any>;
}

const STEP_CONFIG: Record<string, StepDefinition> = {
	login: {
		label: 'Login',
		icon: 'lock',
		Content: AnytimeTicketLoginStep,
		Footer: AnytimeTicketLoginStep.Footer,
	},
	select_product: {
		label: 'Select Product',
		Content: AnytimeTicketProductStep,
		Footer: AnytimeTicketProductStep.Footer,
	},
	register_customer: {
		label: 'New Customer',
		Content: AnytimeTicketRegisterCustomerStep,
		Footer: AnytimeTicketRegisterCustomerStep.Footer,
	},
	terms: {
		label: 'Terms',
		Content: AnytimeTicketTermsStep,
		Footer: AnytimeTicketTermsStep.Footer,
	},
	review: {
		label: 'Review',
		Content: AnytimeTicketReviewStep,
		Footer: AnytimeTicketReviewStep.Footer,
	},
	payment: {
		label: 'Payment',
		Content: AnytimeTicketPaymentStep,
		Footer: AnytimeTicketPaymentStep.Footer,
	},
	success: {
		label: 'Complete',
		Content: AnytimeTicketSuccessStep,
		Footer: AnytimeTicketSuccessStep.Footer,
	},
};

export type StepKey = keyof typeof STEP_CONFIG;

export interface AnytimeTicketFooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollToTop: () => void;
}

interface Props {
	isOpen: boolean;
	onClose: () => void;
	prefillCustomerId?: string;
	prefillCustomerName?: string;
}

function AnytimeTicketModalInner({ isOpen, onClose, prefillCustomerId, prefillCustomerName }: Props) {
	// State from Zustand
	const step = useAnytimeTicketStore(s => s.step);

	// Actions from Zustand
	const storeApi = useAnytimeTicketStoreApi();
	const { selectProduct, selectCustomer } = storeApi.getState();

	// Infrastructure Hooks
	const { handleClose } = useAnytimeTicketNavigationGuard({ isOpen, onActualClose: onClose });

	const scrollModalToTop = useCallback(() => {
		const modalBody = document.querySelector('.purchase-gift-voucher-modal .new-modal__message');
		modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// but we still need to initialize prefilled data if necessary
	useEffect(() => {
		if (isOpen && prefillCustomerId) {
			const { selectedCustomerId } = storeApi.getState();

			if (selectedCustomerId !== prefillCustomerId) {
				selectProduct(null);
				// Construct a minimal user object for prefill
				selectCustomer({
					id: prefillCustomerId,
					firstName: prefillCustomerName || 'Selected',
					lastName: '',
					email: '',
				});
			}
		}
	}, [isOpen, prefillCustomerId, prefillCustomerName, selectProduct, selectCustomer, storeApi]);

	const needsLogin = !globalStore.loggedIn;
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const { data: productData } = useProductList();
	const selectedProduct = productData?.items.find(p => p.id === selectedProductId);
	const hasTerms = !!(selectedProduct?.termsAndConditions || whiteLabelStore.config?.defaultProductTerms);

	// Filter down to the steps that are actually active in the current flow
	const activeStepKeys = Object.keys(STEP_CONFIG).filter(key => {
		if (key === 'login' && !needsLogin) return false;
		if (key === 'terms' && !hasTerms) return false;
		// A detour from the product step — never shown in the step indicator.
		if (key === 'register_customer') return false;
		return true;
	});

	// Map active steps to the format expected by the StepIndicator
	const steps = activeStepKeys.map(key => {
		const { label, icon } = STEP_CONFIG[key];
		return icon ? { label, icon } : label;
	});

	// While on the register-customer detour, the indicator stays on the product step.
	const currentStepIndex = activeStepKeys.indexOf(step === 'register_customer' ? 'select_product' : step);

	const { Content, Footer } = STEP_CONFIG[step];

	return (
		<NewModal
			isOpen={isOpen}
			label={getMessageByKey('anytime-ticket-purchase__modal__title', 'Purchase Passenger Tickets') ?? undefined}
			onRequestClose={handleClose}
			modalSize="md"
			className="purchase-gift-voucher-modal"
			shouldCloseOnOverlayClick={false}
			shouldCloseOnEsc={false}
		>
			<NewModal.Header>
				<GiftVoucherStepIndicator steps={steps} currentStepIndex={currentStepIndex} />
			</NewModal.Header>
			<AnytimeTicketTimer />
			<NewModal.Body>
				<Content onClose={handleClose} />
			</NewModal.Body>
			<Footer onCancel={handleClose} onDone={handleClose} scrollModalToTop={scrollModalToTop} />
		</NewModal>
	);
}

export default function AnytimeTicketModal(props: Props) {
	if (!props.isOpen) return null;
	return (
		<AnytimeTicketProvider>
			<PaymentCheckOutProvider>
				<AnytimeTicketModalInner {...props} />
			</PaymentCheckOutProvider>
		</AnytimeTicketProvider>
	);
}
